// 2026-08-19: live TMDB auto-fill for the bulk title-entry push (Chris x Pascal,
// 2026-08-19). Movies only for now -- series lookup is a phase-two follow-up.
// 2026-08-20: TV series support added -- see fetchTmdbTvSeasonDetails below.
//
// Requires REACT_APP_TMDB_API_KEY set at build time (.env.local, gitignored -- see
// .env.example). This key ends up in the built client JS bundle; see .env.example for
// why that's an accepted tradeoff here.

const TMDB_API_KEY = process.env.REACT_APP_TMDB_API_KEY;
const TMDB_BASE = 'https://api.themoviedb.org/3';
const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p/w500';

// Extracts the numeric TMDB movie id from a "/movie/184-jackie-brown" style path
// (already normalized by urlParsing.js's extractTmdbPath by the time this runs).
// Returns null if it doesn't look like a valid path.
function parseTmdbMovieId(tmdbPath) {
    if (!tmdbPath) {
        return null;
    }
    const match = tmdbPath.match(/^\/movie\/(\d+)/);
    return match ? match[1] : null;
}

// Returns a normalized object on success, or { error: 'not_found' | 'no_api_key' | 'fetch_failed' }.
export async function fetchTmdbMovieDetails(tmdbPath) {
    if (!TMDB_API_KEY) {
        return { error: 'no_api_key' };
    }

    const movieId = parseTmdbMovieId(tmdbPath);
    if (!movieId) {
        return { error: 'not_found' };
    }

    let response;
    try {
        response = await fetch(`${TMDB_BASE}/movie/${movieId}?api_key=${TMDB_API_KEY}&append_to_response=credits`);
    } catch (e) {
        return { error: 'fetch_failed' };
    }

    if (!response.ok) {
        return { error: response.status === 404 ? 'not_found' : 'fetch_failed' };
    }

    const data = await response.json();
    const crew = (data.credits && data.credits.crew) || [];
    const cast = (data.credits && data.credits.cast) || [];

    const releaseYear = data.release_date ? parseInt(data.release_date.substring(0, 4)) : undefined;
    const runtimeMinutes = (typeof data.runtime === 'number' && data.runtime > 0) ? data.runtime : undefined;

    return {
        tmdbTitle: data.title,
        overview: data.overview || undefined,
        releaseYear,
        runtimeMinutes,
        imdbId: data.imdb_id || undefined,
        posterUrl: data.poster_path ? `${TMDB_IMAGE_BASE}${data.poster_path}` : undefined,
        languageCode: data.original_language || undefined,
        countryCode: (data.production_countries && data.production_countries.length > 0) ? data.production_countries[0].iso_3166_1 : undefined,
        directors: crew.filter(c => c.job === 'Director').map(c => c.name),
        writers: [...new Set(crew.filter(c => c.department === 'Writing').map(c => c.name))],
        // Confirmed against real TMDB crew data (The Odyssey, id 1368337, 2026-08-19):
        // Cinematographer = "Director of Photography" (Camera dept), Editor = "Editor"
        // (Editing dept), Composer = "Original Music Composer" (Sound dept) -- not just
        // "Music", which covers other, non-composer song/score credits.
        cinematographers: crew.filter(c => c.job === 'Director of Photography').map(c => c.name),
        editors: crew.filter(c => c.job === 'Editor').map(c => c.name),
        composers: crew.filter(c => c.job === 'Original Music Composer').map(c => c.name),
        // Only the exact job "Producer" -- deliberately excludes Executive Producer,
        // Co-Producer, Associate Producer, Line Producer, etc. Capped at 3.
        producers: crew
            .filter(c => c.job === 'Producer')
            .slice(0, 3)
            .map(c => ({ name: c.name, title: c.job })),
        // 2026-09-03 (URGENT -- root cause of Add Title hanging forever on ~70
        // titles, ~2 weeks): `role: c.character || undefined` produced a literal
        // `undefined` PROPERTY (not just a falsy display value) whenever a cast
        // member has no character credit -- common for documentaries, where real
        // people appear "as themselves" with a blank character field on TMDB.
        // Confirmed directly against this movie's real data (A Brief History of
        // Time, TMDB id 1358): cast member #7, Norman Dix, has character: ''.
        // Firestore's addDoc() rejects ANY document containing an explicit
        // `undefined` value anywhere, including nested inside an array -- and with
        // no try/catch anywhere in Add Title's save path until today, that
        // rejection left the "Saving record..." spinner stuck forever with zero
        // visible error. Fixed by omitting the `role` key entirely when there's no
        // character credit, instead of setting it to undefined.
        cast: cast.slice(0, 7).map(c => c.character ? { name: c.name, role: c.character } : { name: c.name }),
    };
}

// Extracts the numeric TMDB series id from a "/tv/1396-breaking-bad" style path
// (already normalized by urlParsing.js's extractTmdbTvPath by the time this runs).
function parseTmdbTvId(tmdbPath) {
    if (!tmdbPath) {
        return null;
    }
    const match = tmdbPath.match(/^\/tv\/(\d+)/);
    return match ? match[1] : null;
}

// Dedups a list of {name, episode_count} into the distinct names, sorted by episode
// count descending -- used both for aggregating per-episode crew credits (Director/
// Writer, which genuinely vary episode to episode -- see comment below) and for
// picking the highest-episode-count person(s) out of the series' full aggregate crew
// list for jobs assumed stable across a season (Cinematographer/Editor/Composer/
// Producer), since that list isn't returned in any prominence order.
function topNamesByEpisodeCount(entries) {
    const byName = new Map();
    entries.forEach(({ name, episodeCount }) => {
        byName.set(name, Math.max(byName.get(name) || 0, episodeCount || 0));
    });
    return [...byName.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([name]) => name);
}

// Returns a normalized object on success, or { error: 'not_found' | 'no_api_key' | 'fetch_failed' }.
// tmdbPath: "/tv/1396-breaking-bad" (already through extractTmdbTvPath). seasonNumber:
// integer, 1-based.
export async function fetchTmdbTvSeasonDetails(tmdbPath, seasonNumber) {
    if (!TMDB_API_KEY) {
        return { error: 'no_api_key' };
    }

    const seriesId = parseTmdbTvId(tmdbPath);
    // 2026-08-29: explicit undefined/null check, NOT a truthy check -- season 0
    // ("Specials" on TMDB, added this session) is a legitimate, real season and is
    // falsy in JS, so `!seasonNumber` would silently reject every Specials request.
    if (!seriesId || seasonNumber === undefined || seasonNumber === null) {
        return { error: 'not_found' };
    }

    let seriesRes, seasonRes, externalIdsRes, creditsRes;
    try {
        [seriesRes, seasonRes, externalIdsRes, creditsRes] = await Promise.all([
            fetch(`${TMDB_BASE}/tv/${seriesId}?api_key=${TMDB_API_KEY}`),
            fetch(`${TMDB_BASE}/tv/${seriesId}/season/${seasonNumber}?api_key=${TMDB_API_KEY}`),
            fetch(`${TMDB_BASE}/tv/${seriesId}/external_ids?api_key=${TMDB_API_KEY}`),
            fetch(`${TMDB_BASE}/tv/${seriesId}/aggregate_credits?api_key=${TMDB_API_KEY}`),
        ]);
    } catch (e) {
        return { error: 'fetch_failed' };
    }

    if (!seriesRes.ok) {
        return { error: seriesRes.status === 404 ? 'not_found' : 'fetch_failed' };
    }
    if (!seasonRes.ok) {
        // Series exists but this season doesn't (e.g. a season number beyond what
        // this show has) -- distinct from the series itself not existing.
        return { error: 'season_not_found' };
    }

    const series = await seriesRes.json();
    const season = await seasonRes.json();
    const externalIds = externalIdsRes.ok ? await externalIdsRes.json() : {};
    const aggCredits = creditsRes.ok ? await creditsRes.json() : { cast: [], crew: [] };

    const episodes = season.episodes || [];

    // Episode duration: TMDB's per-episode runtime for the selected season, averaged
    // -- more reliable than the series-level episode_run_time, which is frequently
    // empty (confirmed empty for Breaking Bad, 2026-08-20) and even when present is
    // an all-seasons array, not scoped to the one season being added.
    const episodeRuntimes = episodes.map(e => e.runtime).filter(r => typeof r === 'number' && r > 0);
    const runtimeMinutes = episodeRuntimes.length > 0
        ? Math.round(episodeRuntimes.reduce((a, b) => a + b, 0) / episodeRuntimes.length)
        : undefined;

    // Director and Writer genuinely vary per episode within a season (confirmed
    // against Breaking Bad season 3: 10 distinct directors, 8 distinct writers across
    // 13 episodes -- an episode-1-only proxy would badly misrepresent who actually
    // worked on the season). Aggregated here as the full distinct set across every
    // episode in the season, using the per-episode `crew` array the season endpoint
    // already returns -- confirmed present with no extra per-episode API calls
    // needed. Chris can edit/correct the resulting list same as any other field.
    const directorEntries = [];
    const writerEntries = [];
    episodes.forEach(ep => {
        (ep.crew || []).forEach(c => {
            if (c.job === 'Director') directorEntries.push({ name: c.name, episodeCount: 1 });
            if (c.department === 'Writing') writerEntries.push({ name: c.name, episodeCount: 1 });
        });
    });
    const directors = topNamesByEpisodeCount(directorEntries);
    const writers = topNamesByEpisodeCount(writerEntries);

    // Cinematographer/Editor/Composer/Producer: pulled from the series-wide aggregate
    // credits (not per-episode) since these are expected to be stable across a season
    // -- confirmed for Composer on Breaking Bad (one person, all 62 episodes) and
    // Cinematographer (one person covering the large majority of episodes, a few
    // guest DPs on individual episodes). aggCredits.crew entries aren't returned in
    // any prominence order, hence sorting by episode_count via topNamesByEpisodeCount.
    const aggCrew = aggCredits.crew || [];
    const namesForJob = (jobName) => topNamesByEpisodeCount(
        aggCrew
            .filter(c => (c.jobs || []).some(j => j.job === jobName))
            .map(c => ({ name: c.name, episodeCount: Math.max(...c.jobs.filter(j => j.job === jobName).map(j => j.episode_count || 0)) }))
    );
    const cinematographers = namesForJob('Director of Photography');
    const editors = namesForJob('Editor');
    const composers = namesForJob('Original Music Composer');
    // Only the exact job "Producer", same exclusion as movies (Executive/Co/Associate/
    // Line Producer all deliberately excluded). Capped at 3, highest episode count first.
    const producers = namesForJob('Producer').slice(0, 3).map(name => ({ name, title: 'Producer' }));

    // 2026-09-03: same fix as the movie cast mapping above -- omit `role` entirely
    // rather than setting it to undefined when there's no character credit
    // (documentary/reality TV series commonly have cast with no character field).
    const cast = (aggCredits.cast || [])
        .slice(0, 7)
        .map(c => {
            const character = c.roles && c.roles[0] && c.roles[0].character;
            return character ? { name: c.name, role: character } : { name: c.name };
        });

    return {
        seriesName: series.name,
        seasonNumber,
        // 2026-08-20: Display title format confirmed with Pascal -- series name +
        // season, since each season is its own catalog entry and needs to be
        // distinguishable in list views. Note: this makes multi-season adds of the
        // same show look similar to each other in the duplicate-title check (PR #15)
        // by design -- flagged separately, not fixed here.
        // 2026-08-29: the actual title text is now built by the caller
        // (TitleAddPage.js's populateFromTmdbTv) from seriesName + a seasonLabel that
        // can say "Specials" or "Limited Series" instead of "Season N" -- this
        // function has no concept of either, so it no longer builds displayTitle
        // itself.
        overview: season.overview || series.overview || undefined,
        posterUrl: (season.poster_path || series.poster_path) ? `${TMDB_IMAGE_BASE}${season.poster_path || series.poster_path}` : undefined,
        numEpisodes: episodes.length || undefined,
        // 2026-08-20 (added after Chris's testing -- Year of Release wasn't pulling
        // through at all): the season's own air date, not the series' first_air_date --
        // each season aired in its own year, so this is the correct per-entry value,
        // same relationship the movie flow has between releaseYear and release_date.
        releaseYear: season.air_date ? parseInt(season.air_date.substring(0, 4)) : undefined,
        runtimeMinutes,
        imdbId: externalIds.imdb_id || undefined,
        // origin_country is a list; take the first, same single-country model movies use.
        countryCode: (series.origin_country && series.origin_country.length > 0) ? series.origin_country[0] : undefined,
        languageCode: series.original_language || undefined,
        showrunners: (series.created_by || []).map(p => p.name),
        directors,
        writers,
        cinematographers,
        editors,
        composers,
        producers,
        cast,
    };
}
